import type {
  SubtitleEditorData,
  SubtitleEditorLogoData,
} from '@/components/SubtitleEditor';
import { resolveRenderFont, type RenderFontCandidate } from '@/lib/resolveRenderFont';
import { pickSubtitleOutline } from '@/lib/subtitles/subtitleOutline';

export interface SubtitleLogoRenderConfig {
  subtitle_config?: Record<string, unknown>;
  logo_config?: Record<string, unknown>;
}

/** The subtitle + logo part of a clip render input, from the editor's style
 *  state. Everything the preview draws must be sent here, or the export
 *  silently diverges from what the user saw. */
export function buildSubtitleLogoRenderConfig({
  subtitlesEnabled,
  subtitleData,
  logoData,
  selectedSubtitleFontId,
  fonts,
}: {
  subtitlesEnabled: boolean;
  subtitleData: SubtitleEditorData;
  logoData: SubtitleEditorLogoData;
  selectedSubtitleFontId: string | null | undefined;
  fonts: readonly RenderFontCandidate[] | null | undefined;
}): SubtitleLogoRenderConfig {
  const extras: SubtitleLogoRenderConfig = {};
  if (subtitlesEnabled) {
    // subtitleData.fontFamily may hold the font's display name (picker /
    // restored config); the render contract wants the documentId.
    const renderFont = resolveRenderFont({
      selectedFontId: selectedSubtitleFontId,
      fontFamily: subtitleData.fontFamily,
      fonts,
    });
    extras.subtitle_config = {
      fontFamily: renderFont.fontFamily,
      fontUrl: renderFont.fontUrl,
      fontSize: subtitleData.fontSize,
      textColor: subtitleData.textColor,
      highlightColor: subtitleData.highlightColor,
      highlightEnabled: subtitleData.highlightEnabled,
      shadowEnabled: subtitleData.shadowEnabled,
      shadowColor: subtitleData.shadowColor,
      shadowSize: subtitleData.shadowSize,
      ...pickSubtitleOutline(subtitleData),
      backgroundEnabled: subtitleData.backgroundEnabled,
      backgroundColor: subtitleData.backgroundColor,
      highlightBoxEnabled: subtitleData.highlightBoxEnabled,
      highlightBoxBackgroundColor: subtitleData.highlightBoxBackgroundColor,
      highlightBoxBorderRadius: subtitleData.highlightBoxBorderRadius,
      animation: subtitleData.animation,
      // Without this, the worker falls back to the brand kit's default
      // (or 6) and the rendered line breaks won't match the user's preview.
      maxWordsPerLine: subtitleData.maxWordsPerLine,
      textTransform: subtitleData.textTransform,
      removePunctuation: subtitleData.removePunctuation,
      // Position/margins/background sizing were never sent, so exports
      // silently ignored them however the preview looked.
      position: subtitleData.position,
      marginVertical: subtitleData.marginVertical,
      marginHorizontal: subtitleData.marginHorizontal,
      textAlign: subtitleData.textAlign,
      backgroundOpacity: subtitleData.backgroundOpacity,
      backgroundWidth: subtitleData.backgroundWidth,
      backgroundHeight: subtitleData.backgroundHeight,
      highlightBoxPaddingX: subtitleData.highlightBoxPaddingX,
      highlightBoxPaddingY: subtitleData.highlightBoxPaddingY,
    };
  }
  if (logoData.url) {
    extras.logo_config = {
      logo_image_url: logoData.url,
      logo_position: logoData.position,
      logo_size: logoData.size,
      logo_opacity: logoData.opacity,
      logo_horizontal_margin: logoData.horizontalMargin,
      logo_vertical_margin: logoData.verticalMargin,
    };
  } else {
    // Omission inherits a template logo on the backend. An editor with no
    // logo must explicitly clear that fallback to match its preview.
    extras.logo_config = { logo_image_url: null };
  }
  return extras;
}
